import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Mail } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import ShowFor from '@Components/ShowFor';
import { useEventReport, useEventStaff } from '@Hooks/useEventReports';
import type {
  EventReportVerdict,
  EventReportAttendee,
} from '@Types/eventReport';
import MessageComposer from './MessageComposer';

const VERDICT_LABEL: Record<EventReportVerdict, string> = {
  SELLOUT: 'Sold out',
  STRONG: 'Strong',
  SOFT: 'Soft',
  UNDERSOLD: 'Undersold',
  NO_CAPACITY: 'No capacity set',
};

const money = (v: number) =>
  v.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const pct = (v: number | null) =>
  v == null ? '—' : `${Math.round(v * 100)}%`;

interface Props {
  reportId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-zinc-800">{value}</div>
    </div>
  );
}

export default function EventReportSheet({
  reportId,
  open,
  onOpenChange,
}: Props) {
  const { data: report, status } = useEventReport({
    id: reportId ?? undefined,
    enabled: open && !!reportId,
  });
  const eventDate = report ? DateTime.fromISO(report.eventDate) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{report?.eventName ?? 'Event Report'}</SheetTitle>
          {report && (
            <p className="text-xs text-zinc-500">
              {VERDICT_LABEL[report.status]}
              {eventDate?.isValid && <> · {eventDate.toFormat('cccc, LLL d')}</>}
            </p>
          )}
        </SheetHeader>
        <SheetBody className="space-y-6">
          {status === 'pending' ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : !report ? (
            <p className="text-sm text-zinc-500">Report not found.</p>
          ) : (
            <>
              {/* Headline metrics */}
              <div className="grid grid-cols-2 gap-3">
                <Stat
                  label="Tickets sold"
                  value={
                    report.metrics.capacity
                      ? `${report.metrics.ticketsSold} / ${report.metrics.capacity}`
                      : `${report.metrics.ticketsSold}`
                  }
                />
                <Stat label="Sell-through" value={pct(report.metrics.sellThrough)} />
                <Stat label="Gross revenue" value={money(report.revenue.gross)} />
                <Stat label="Net revenue" value={money(report.revenue.net)} />
              </div>

              {/* Buyers */}
              <div className="rounded-lg border border-zinc-200 p-4">
                <div className="text-xs font-bold tracking-wide text-zinc-500 uppercase">
                  Buyers
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-700">
                  <span>{report.metrics.uniqueBuyers} unique</span>
                  <span>{report.metrics.memberBuyers} members</span>
                  <span>{report.metrics.nonMemberBuyers} non-members</span>
                  {report.revenue.discount > 0 && (
                    <span>{money(report.revenue.discount)} in discounts</span>
                  )}
                </div>
                {report.metrics.compTickets > 0 && (
                  <p className="mt-2 text-xs text-zinc-500">
                    {report.metrics.compTickets} comp / guest-list ticket
                    {report.metrics.compTickets === 1 ? '' : 's'} (counted as
                    attendance, excluded from revenue).
                  </p>
                )}
              </div>

              {/* Tier breakdown */}
              {report.tiers.length > 0 && (
                <div>
                  <div className="mb-2 text-xs font-bold tracking-wide text-zinc-500 uppercase">
                    By ticket type
                  </div>
                  <div className="overflow-hidden rounded-lg border border-zinc-200">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 text-left text-xs text-zinc-500">
                        <tr>
                          <th className="px-3 py-2 font-medium">Tier</th>
                          <th className="px-3 py-2 text-right font-medium">Sold</th>
                          <th className="px-3 py-2 text-right font-medium">Price</th>
                          <th className="px-3 py-2 text-right font-medium">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {report.tiers.map((tier) => (
                          <tr key={tier.name}>
                            <td className="px-3 py-2 text-zinc-800">{tier.name}</td>
                            <td className="px-3 py-2 text-right text-zinc-600">
                              {tier.sold}/{tier.available}
                            </td>
                            <td className="px-3 py-2 text-right text-zinc-600">
                              {money(tier.price)}
                            </td>
                            <td className="px-3 py-2 text-right text-zinc-600">
                              {money(tier.listRevenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-400">
                    Tier revenue is list-price based; gross/net above are the
                    authoritative totals and exclude any post-event refunds.
                  </p>
                </div>
              )}

              {/* Attendees + inline messaging */}
              {report.attendees.length > 0 && (
                <AttendeeMessagePanel
                  eventName={report.eventName}
                  attendees={report.attendees}
                />
              )}

              {/* Staff who worked the event (live from the warehouse) */}
              {reportId && (
                <StaffPanel
                  reportId={reportId}
                  open={open}
                  eventName={report.eventName}
                />
              )}

              {/* Narrative recap */}
              {report.narrative && (
                <div>
                  <div className="mb-2 text-xs font-bold tracking-wide text-zinc-500 uppercase">
                    Recap
                  </div>
                  <pre className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs whitespace-pre-wrap text-zinc-700">
                    {report.narrative}
                  </pre>
                </div>
              )}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Staff who worked the event, read live from the warehouse. Its own query so a
 * slow/unavailable warehouse never blocks the rest of the recap. Staff who map to
 * a fish record can be messaged (the others have no campaign-addressable identity).
 */
function StaffPanel({
  reportId,
  open,
  eventName,
}: {
  reportId: string;
  open: boolean;
  eventName: string;
}) {
  const { data: staff, status, isError } = useEventStaff({
    id: reportId,
    enabled: open,
  });
  const [composing, setComposing] = useState(false);

  const messageableIds = (staff ?? [])
    .map((s) => s.fishId)
    .filter((id): id is string => !!id);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-bold tracking-wide text-zinc-500 uppercase">
          Staff who worked
          {staff && staff.length > 0 ? ` (${staff.length})` : ''}
        </span>
        {messageableIds.length > 0 && (
          <ShowFor permissions={['CAMPAIGNS_WRITE']}>
            <Button
              size="sm"
              variant={composing ? 'outline' : 'primary'}
              onClick={() => setComposing((c) => !c)}
            >
              <Mail className="mr-1.5 h-4 w-4" />
              {composing ? 'Close' : `Message staff (${messageableIds.length})`}
            </Button>
          </ShowFor>
        )}
      </div>

      {composing && messageableIds.length > 0 && (
        <MessageComposer
          fishIds={messageableIds}
          campaignTitle={`${eventName} — Staff`}
          recipientLabel="staff member"
          onSent={() => setComposing(false)}
        />
      )}

      {status === 'pending' ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : isError ? (
        <p className="rounded-lg border border-zinc-200 px-3 py-2 text-sm text-zinc-500">
          Couldn't load staff for this event.
        </p>
      ) : !staff || staff.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-4 text-center text-sm text-zinc-500">
          No staff records found for this date.
        </p>
      ) : (
        <div className="max-h-72 divide-y divide-zinc-100 overflow-y-auto rounded-lg border border-zinc-200">
          {staff.map((s) => (
            <div
              key={s.eeId}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-zinc-800">{s.name}</span>
                {s.position && (
                  <span className="truncate text-xs text-zinc-500">
                    {s.position}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-right text-xs text-zinc-500">
                {s.hoursWorked.toLocaleString('en-US', {
                  maximumFractionDigits: 1,
                })}{' '}
                hrs
                {s.daysWorked > 1 ? ` · ${s.daysWorked} days` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Attendee roster plus an inline composer that messages every attendee by their
 * explicit fishIds — no navigation off the desk.
 */
function AttendeeMessagePanel({
  eventName,
  attendees,
}: {
  eventName: string;
  attendees: EventReportAttendee[];
}) {
  const [composing, setComposing] = useState(false);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-bold tracking-wide text-zinc-500 uppercase">
          Attendees ({attendees.length})
        </span>
        <ShowFor permissions={['CAMPAIGNS_WRITE']}>
          <Button
            size="sm"
            variant={composing ? 'outline' : 'primary'}
            onClick={() => setComposing((c) => !c)}
          >
            <Mail className="mr-1.5 h-4 w-4" />
            {composing ? 'Close' : 'Message attendees'}
          </Button>
        </ShowFor>
      </div>

      {composing && (
        <MessageComposer
          fishIds={attendees.map((a) => a.fishId)}
          campaignTitle={`${eventName} — Attendees`}
          recipientLabel="attendee"
          onSent={() => setComposing(false)}
        />
      )}

      {/* Roster */}
      <div className="max-h-72 divide-y divide-zinc-100 overflow-y-auto rounded-lg border border-zinc-200">
        {attendees.map((a) => (
          <div
            key={a.fishId}
            className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-zinc-800">{a.name}</span>
              {a.isMember && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold tracking-wide text-emerald-700 uppercase">
                  Member
                </span>
              )}
            </span>
            <span className="shrink-0 text-xs text-zinc-500">
              {a.ticketCount} ticket{a.ticketCount === 1 ? '' : 's'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
